/**
 * Systems-analysis views — single source of truth.
 *
 * These are the "Kumu-like" systems-analysis surfaces recommended in
 * specs/systems-analysis-visualisation.md. Each is gated behind a per-instance
 * feature flag stored at `kuutio_instances.metadata.featureFlags[key]`.
 *
 * Flags default to DISABLED (off) — a view only appears once an admin enables it
 * for that instance. The switch is a "prepare" control in the manifest of the
 * module the view belongs to (admin.controls, featureFlags.<key>; shown in the
 * flow builder), and the route gating (SystemsViewScaffold) reads THIS list.
 *
 * Adding a new view:
 *   1. Add an entry below (unique `key`, `route`, Finnish `label`/`description`).
 *   2. Add the <Route> in App.jsx pointing at a screen wrapped in
 *      <SystemsViewScaffold viewKey="...">.
 *   3. Add a { key: 'featureFlags.<key>', store: 'metadata', type: 'toggle',
 *      when: 'prepare', … } control to the owning module's manifest.
 */

export const SYSTEMS_ANALYSIS_VIEWS = [
  {
    key: 'pivotNetwork',
    route: '/systems/pivot-network',
    phase: 1,
    icon: 'Network',
    label: 'Vipupisteiden verkostoanalyysi',
    description:
      'Rakenteellinen analyysi vaikutuspolkujen verkostosta: solmujen keskeisyys ja polkujen yhtymäkohdat vahvistavat ja perustelevat vipupisteiden tunnistuksen.',
    locatedIn: 'Vipupisteet (päivä 2)',
  },
  {
    key: 'systemsMap',
    route: '/systems/map',
    phase: 2,
    icon: 'Map',
    label: 'Systeemikartta',
    description:
      'Kausaalinen systeemikartta merkittyine yhteyksineen (vahvistaa/heikentää), takaisinkytkentäsilmukoineen ja este-solmuineen. Vaihtoehto lineaariselle vaikutuskartalle.',
    locatedIn: 'Päivä 1 / Systeemikartta',
  },
  {
    key: 'causalLoops',
    route: '/systems/causal-loops',
    phase: 3,
    icon: 'RefreshCw',
    label: 'Takaisinkytkentäkartta',
    description:
      'Syy-seurauskehät — vahvistavat ja tasapainottavat silmukat — vaikutuspoluista ja linssien havainnoista. Tekoälyavusteinen ehdotus, jonka fasilitaattori kuratoi.',
    locatedIn: 'Vipupisteet / Entäpä jos (päivä 2)',
  },
  {
    key: 'actorMap',
    route: '/systems/actors',
    phase: 4,
    icon: 'Users',
    label: 'Toimijakartta',
    description:
      'Toimijoiden ja organisaatioiden vaikutussuhteet ja kytkennät ratkaisuihin. Voi sisältää myös henkilöitä — huom. GDPR: henkilötaso vaatii suostumuksen ja erillisen harkinnan.',
    locatedIn: 'Ratkaisut / Organisaatiot',
  },
];

/** Read the featureFlags object off a loaded instance (safe for null/undefined). */
export function getFeatureFlags(instance) {
  return instance?.metadata?.featureFlags || {};
}

/** Is a given systems-analysis view enabled for this instance? Defaults to false. */
export function isViewEnabled(instance, key) {
  return Boolean(getFeatureFlags(instance)[key]);
}

/** Look up a view definition by its flag key. */
export function getViewByKey(key) {
  return SYSTEMS_ANALYSIS_VIEWS.find((v) => v.key === key) || null;
}
